import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { X, RefreshCw, Search, UserPlus, CheckCircle, Plus } from 'lucide-react';
import { toast } from 'sonner';
import apiClient from '../../utils/api';

const AdminCreateBooking = ({ onClose, onBookingCreated }) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);
  const [isNewUser, setIsNewUser] = useState(false);
  const [newUserName, setNewUserName] = useState('');
  const [newUserEmail, setNewUserEmail] = useState('');
  const [classes, setClasses] = useState([]);
  const [selectedClass, setSelectedClass] = useState(null);
  const [spots, setSpots] = useState(1);
  const [paymentMethod, setPaymentMethod] = useState('cash');
  const [discountCode, setDiscountCode] = useState('');
  const [sendConfirmation, setSendConfirmation] = useState(true);
  const [copyToAdmin, setCopyToAdmin] = useState(false);
  const [searching, setSearching] = useState(false);
  const [creating, setCreating] = useState(false);
  const [loadingClasses, setLoadingClasses] = useState(true);

  // Fetch available classes
  useEffect(() => {
    const fetchClasses = async () => {
      try {
        const response = await apiClient.get('/classes');
        // Filter to only future classes with available spots
        const now = new Date();
        const availableClasses = response.data.filter(cls => {
          const classDate = new Date(cls.class_datetime);
          return classDate > now && cls.spots_available > 0;
        });
        setClasses(availableClasses);
      } catch (error) {
        console.error('Failed to fetch classes:', error);
        toast.error('Failed to load classes');
      } finally {
        setLoadingClasses(false);
      }
    };
    fetchClasses();
  }, []);

  // Search users
  useEffect(() => {
    const searchUsers = async () => {
      if (searchQuery.length < 2) {
        setSearchResults([]);
        return;
      }
      
      setSearching(true);
      try {
        const response = await apiClient.get(`/admin/users/search?q=${encodeURIComponent(searchQuery)}`);
        setSearchResults(response.data);
      } catch (error) {
        console.error('Failed to search users:', error);
      } finally {
        setSearching(false);
      }
    };

    const debounce = setTimeout(searchUsers, 300);
    return () => clearTimeout(debounce);
  }, [searchQuery]);

  const handleSelectUser = (user) => {
    setSelectedUser(user);
    setIsNewUser(false);
    setSearchQuery('');
    setSearchResults([]);
  };

  const handleCreateNewUser = () => {
    setIsNewUser(true);
    setSelectedUser(null);
    setNewUserEmail(searchQuery.includes('@') ? searchQuery : '');
    setSearchQuery('');
    setSearchResults([]);
  };

  const handleCancelNewUser = () => {
    setIsNewUser(false);
    setNewUserName('');
    setNewUserEmail('');
  };

  const handleSelectClass = (cls) => {
    setSelectedClass(cls);
    // Reset spots if more than available
    if (spots > cls.spots_available) {
      setSpots(cls.spots_available);
    }
  };

  const handleCreateBooking = async () => {
    // Validate user selection
    if (!selectedUser && !isNewUser) {
      toast.error('Please select a user or create a new one');
      return;
    }
    if (isNewUser) {
      if (!newUserEmail || !newUserEmail.includes('@')) {
        toast.error('Please enter a valid email address');
        return;
      }
      if (!newUserName.trim()) {
        toast.error('Please enter the user\'s name');
        return;
      }
    }
    if (!selectedClass) {
      toast.error('Please select a class');
      return;
    }

    setCreating(true);
    try {
      const payload = {
        user_email: isNewUser ? newUserEmail : selectedUser.email,
        user_name: isNewUser ? newUserName.trim() : null,
        class_id: selectedClass.class_id,
        spots,
        payment_method: paymentMethod,
        discount_code: discountCode || null,
        send_confirmation: sendConfirmation,
        copy_to_admin: copyToAdmin
      };

      const response = await apiClient.post('/admin/bookings/create', payload);

      if (response.data.user_created) {
        toast.success(`New user created: ${response.data.user_name}`);
      }
      toast.success(`Booking created for ${response.data.user_name}`);
      if (response.data.email_sent) {
        toast.success('Confirmation email sent');
      }
      
      if (onBookingCreated) {
        onBookingCreated();
      }
      onClose();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create booking');
    } finally {
      setCreating(false);
    }
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-GB', {
      weekday: 'short',
      day: 'numeric',
      month: 'short',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        className="bg-background-paper border border-white/10 rounded-sm p-8 max-w-2xl w-full max-h-[90vh] overflow-y-auto"
      >
        <div className="flex justify-between items-center mb-6">
          <h3 className="font-heading text-xl font-bold">
            CREATE <span className="text-primary">BOOKING</span>
          </h3>
          <Button variant="ghost" size="sm" onClick={onClose}>
            <X className="w-5 h-5" />
          </Button>
        </div>

        {/* Step 1: Select User */}
        <div className="mb-6">
          <Label className="text-sm font-bold mb-2 block">1. SELECT USER</Label>
          
          {selectedUser ? (
            <div className="flex items-center justify-between p-4 bg-primary/10 border border-primary/30 rounded-sm">
              <div>
                <p className="font-bold">{selectedUser.name}</p>
                <p className="text-sm text-neutral-400">{selectedUser.email}</p>
              </div>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setSelectedUser(null)}
              >
                Change
              </Button>
            </div>
          ) : isNewUser ? (
            <div className="p-4 bg-green-500/10 border border-green-500/30 rounded-sm space-y-3">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Plus className="w-4 h-4 text-green-500" />
                  <span className="font-bold text-green-500">Creating New User</span>
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={handleCancelNewUser}
                  className="text-neutral-400 hover:text-white"
                >
                  Cancel
                </Button>
              </div>
              <div>
                <Label htmlFor="new-user-name" className="text-sm">Full Name *</Label>
                <Input
                  id="new-user-name"
                  value={newUserName}
                  onChange={(e) => setNewUserName(e.target.value)}
                  placeholder="Enter full name"
                  className="bg-background border-white/10 mt-1"
                  data-testid="new-user-name-input"
                />
              </div>
              <div>
                <Label htmlFor="new-user-email" className="text-sm">Email Address *</Label>
                <Input
                  id="new-user-email"
                  type="email"
                  value={newUserEmail}
                  onChange={(e) => setNewUserEmail(e.target.value)}
                  placeholder="Enter email address"
                  className="bg-background border-white/10 mt-1"
                  data-testid="new-user-email-input"
                />
              </div>
              <p className="text-xs text-neutral-500">
                User will be created and can set their password via "Forgot Password" later.
              </p>
            </div>
          ) : (
            <div className="relative">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-neutral-400" />
                <Input
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Search by email or name..."
                  className="bg-background border-white/10 pl-10"
                  data-testid="user-search-input"
                />
                {searching && (
                  <RefreshCw className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-neutral-400 animate-spin" />
                )}
              </div>
              
              {(searchResults.length > 0 || searchQuery.length >= 2) && (
                <div className="absolute z-10 w-full mt-1 bg-background border border-white/10 rounded-sm max-h-60 overflow-y-auto">
                  {searchResults.map((user) => (
                    <button
                      key={user.user_id}
                      onClick={() => handleSelectUser(user)}
                      className="w-full text-left px-4 py-3 hover:bg-white/5 border-b border-white/5"
                      data-testid={`user-result-${user.user_id}`}
                    >
                      <p className="font-medium">{user.name}</p>
                      <p className="text-sm text-neutral-400">{user.email}</p>
                    </button>
                  ))}
                  
                  {/* Create New User option - always shown when searching */}
                  {searchQuery.length >= 2 && (
                    <button
                      onClick={handleCreateNewUser}
                      className="w-full text-left px-4 py-3 hover:bg-green-500/10 border-t border-white/10 flex items-center gap-2 text-green-500"
                      data-testid="create-new-user-btn"
                    >
                      <Plus className="w-4 h-4" />
                      <div>
                        <p className="font-medium">Create New User</p>
                        <p className="text-xs text-neutral-400">
                          {searchQuery.includes('@') ? `with email: ${searchQuery}` : 'Add a new person to the system'}
                        </p>
                      </div>
                    </button>
                  )}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Step 2: Select Class */}
        <div className="mb-6">
          <Label className="text-sm font-bold mb-2 block">2. SELECT CLASS</Label>
          
          {loadingClasses ? (
            <div className="flex items-center justify-center py-8">
              <RefreshCw className="w-6 h-6 animate-spin text-primary" />
            </div>
          ) : selectedClass ? (
            <div className="flex items-center justify-between p-4 bg-primary/10 border border-primary/30 rounded-sm">
              <div>
                <p className="font-bold">{selectedClass.title}</p>
                <p className="text-sm text-neutral-400">
                  {formatDate(selectedClass.class_datetime)} • {selectedClass.instructor}
                </p>
                <p className="text-sm text-neutral-400">
                  {selectedClass.spots_available} spots available • £{selectedClass.price}
                </p>
              </div>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setSelectedClass(null)}
              >
                Change
              </Button>
            </div>
          ) : (
            <div className="max-h-48 overflow-y-auto border border-white/10 rounded-sm">
              {classes.length === 0 ? (
                <p className="text-neutral-400 text-center py-8">No available classes</p>
              ) : (
                classes.map((cls) => (
                  <button
                    key={cls.class_id}
                    onClick={() => handleSelectClass(cls)}
                    className="w-full text-left px-4 py-3 hover:bg-white/5 border-b border-white/5 last:border-0"
                    data-testid={`class-option-${cls.class_id}`}
                  >
                    <div className="flex justify-between items-center">
                      <div>
                        <p className="font-medium">{cls.title}</p>
                        <p className="text-sm text-neutral-400">
                          {formatDate(cls.class_datetime)} • {cls.instructor}
                        </p>
                      </div>
                      <div className="text-right">
                        <p className="font-bold">£{cls.price}</p>
                        <p className="text-xs text-neutral-400">{cls.spots_available} spots</p>
                      </div>
                    </div>
                  </button>
                ))
              )}
            </div>
          )}
        </div>

        {/* Step 3: Booking Details */}
        <div className="space-y-4 mb-6">
          <Label className="text-sm font-bold block">3. BOOKING DETAILS</Label>
          
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label htmlFor="spots" className="text-sm">Number of Spots</Label>
              <Input
                id="spots"
                type="number"
                min={1}
                max={selectedClass?.spots_available || 10}
                value={spots}
                onChange={(e) => setSpots(Math.max(1, parseInt(e.target.value) || 1))}
                className="bg-background border-white/10 mt-1"
                data-testid="spots-input"
              />
            </div>
            
            <div>
              <Label className="text-sm">Payment Status</Label>
              <div className="flex gap-4 mt-2">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="radio"
                    checked={paymentMethod === 'cash'}
                    onChange={() => setPaymentMethod('cash')}
                    className="w-4 h-4 accent-primary"
                  />
                  <span className="text-sm">Pay at Reception</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="radio"
                    checked={paymentMethod === 'paid'}
                    onChange={() => setPaymentMethod('paid')}
                    className="w-4 h-4 accent-primary"
                  />
                  <span className="text-sm">Already Paid</span>
                </label>
              </div>
            </div>
          </div>

          <div>
            <Label htmlFor="discount" className="text-sm">Discount Code (Optional)</Label>
            <Input
              id="discount"
              value={discountCode}
              onChange={(e) => setDiscountCode(e.target.value.toUpperCase())}
              placeholder="Enter discount code"
              className="bg-background border-white/10 mt-1"
              data-testid="discount-input"
            />
          </div>

          <label className="flex items-center gap-3 cursor-pointer">
            <input
              type="checkbox"
              checked={sendConfirmation}
              onChange={(e) => setSendConfirmation(e.target.checked)}
              className="w-5 h-5 rounded border-white/20 bg-background text-primary focus:ring-primary"
              data-testid="send-confirmation-checkbox"
            />
            <div>
              <span className="font-medium">Send confirmation email</span>
              <p className="text-xs text-neutral-500">User will receive booking details via email</p>
            </div>
          </label>

          {sendConfirmation && (
            <label className="flex items-center gap-3 cursor-pointer ml-8">
              <input
                type="checkbox"
                checked={copyToAdmin}
                onChange={(e) => setCopyToAdmin(e.target.checked)}
                className="w-5 h-5 rounded border-white/20 bg-background text-primary focus:ring-primary"
                data-testid="copy-to-admin-checkbox"
              />
              <div>
                <span className="font-medium">Send copy to me</span>
                <p className="text-xs text-neutral-500">You will receive a copy of the confirmation email</p>
              </div>
            </label>
          )}
        </div>

        {/* Summary */}
        {(selectedUser || isNewUser) && selectedClass && (
          <div className="bg-background p-4 rounded-sm border border-white/10 mb-6">
            <h4 className="font-bold mb-2">Booking Summary</h4>
            <div className="text-sm space-y-1">
              <div className="flex justify-between">
                <span className="text-neutral-400">User:</span>
                <span>
                  {isNewUser ? (
                    <span className="text-green-500">
                      {newUserName || '(name required)'} ({newUserEmail || '(email required)'})
                      <span className="text-xs ml-1">(NEW)</span>
                    </span>
                  ) : (
                    `${selectedUser.name} (${selectedUser.email})`
                  )}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-neutral-400">Class:</span>
                <span>{selectedClass.title}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-neutral-400">Spots:</span>
                <span>{spots}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-neutral-400">Price per spot:</span>
                <span>£{selectedClass.price}</span>
              </div>
              <div className="flex justify-between font-bold text-lg pt-2 border-t border-white/10 mt-2">
                <span>Total:</span>
                <span className="text-primary">£{(selectedClass.price * spots).toFixed(2)}</span>
              </div>
            </div>
          </div>
        )}

        {/* Actions */}
        <div className="flex gap-4">
          <Button
            variant="outline"
            className="flex-1"
            onClick={onClose}
            disabled={creating}
          >
            Cancel
          </Button>
          <Button
            className="flex-1 bg-primary hover:bg-primary/80"
            onClick={handleCreateBooking}
            disabled={creating || (!selectedUser && !isNewUser) || !selectedClass || (isNewUser && (!newUserName.trim() || !newUserEmail))}
            data-testid="create-booking-btn"
          >
            {creating ? (
              <>
                <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                Creating...
              </>
            ) : (
              <>
                <UserPlus className="w-4 h-4 mr-2" />
                {isNewUser ? 'Create User & Book' : 'Create Booking'}
              </>
            )}
          </Button>
        </div>
      </motion.div>
    </div>
  );
};

export default AdminCreateBooking;
